import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalStateCard,
  formatPortalDate,
  usePortalTableStyles
} from './portalShared'
import { STATUS_META, STATUS_OPTIONS } from '../HMI/SubcontractorInvoices/invoiceStatus'
import { isMockMode, mockList } from '../HMI/SubcontractorInvoices/mockData'

const useStyles = makeStyles((theme) => ({
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  statusBadge: {
    display: 'inline-block',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11.5,
    fontWeight: 600,
    fontFamily: 'var(--df-font-mono)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1)
  },
  downloadLink: {
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap'
  }
}))

function formatCurrency (amount) {
  if (amount == null || amount === '') return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function StatusBadge ({ status, classes }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', bg: '#f5f5f5', color: '#555' }
  return (
    <span
      className={classes.statusBadge}
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      {meta.label}
    </span>
  )
}

const SubcontractorInvoices = () => {
  const classes = useStyles()
  const shared = usePortalTableStyles()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [invoices, setInvoices] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [status, setStatus] = useState('')

  const loadInvoices = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const params = { page: page + 1, limit: rowsPerPage }
      if (status) params.status = status

      const response = isMockMode()
        ? { data: await mockList(params) }
        : await axios.get('/api/subcontractor/invoices', { params })
      setInvoices(response.data?.invoices || [])
      setTotal(response.data?.total || 0)
    } catch (requestError) {
      console.error('Failed to load subcontractor invoices', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load invoices right now.')
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, status])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  if (loading && !invoices.length) {
    return <PortalLoadingCard title="Loading invoices" message="Fetching your invoice records..." minHeight={280} />
  }

  return (
    <div>
      {isMockMode() && (
        <Box mb={2} p={1.5} style={{ background: '#fff3d9', color: '#8c5b00', borderRadius: 6, fontSize: 12.5, fontWeight: 600 }}>
          MOCK MODE — all data is in-memory only. Remove <code>?mock=1</code> to use the real API.
        </Box>
      )}
      <Box mb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
        <Typography variant="h6" className={shared.cardTitle}>
          Invoices
        </Typography>
        <div className={classes.filterRow}>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              setPage(0)
            }}
            variant="outlined"
            style={{ minWidth: 140 }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Chip label={`${total} invoice${total !== 1 ? 's' : ''}`} className="df-chip-info" />
        </div>
      </Box>

      {error ? (
        <PortalStateCard
          title="Invoices unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadInvoices}
          tone="danger"
          minHeight={240}
        />
      ) : (
        <Card className={shared.tableCard}>
          <CardContent style={{ padding: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={shared.tableHeadCell}>Invoice #</TableCell>
                    <TableCell className={shared.tableHeadCell}>Site</TableCell>
                    <TableCell className={shared.tableHeadCell} align="right">Acreage</TableCell>
                    <TableCell className={shared.tableHeadCell} align="right">Amount</TableCell>
                    <TableCell className={shared.tableHeadCell}>Status</TableCell>
                    <TableCell className={shared.tableHeadCell}>Date</TableCell>
                    <TableCell className={shared.tableHeadCell}>PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const displayDate = invoice.status === 'paid' && invoice.paidAt
                      ? formatPortalDate(invoice.paidAt, { fallback: '—' })
                      : formatPortalDate(invoice.created_at, { fallback: '—' })
                    const dateLabel = invoice.status === 'paid' && invoice.paidAt ? 'Paid' : 'Created'

                    return (
                      <TableRow key={invoice._id} hover>
                        <TableCell>
                          <Typography variant="body2" className={shared.primaryText}>
                            {invoice.invoiceNumber || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={shared.primaryText}>
                            {invoice.scheduleItem?.siteName || invoice.entity?.name || 'Unnamed Site'}
                          </Typography>
                          {invoice.scheduleItem?.siteAddress ? (
                            <Typography variant="body2" className={shared.secondaryText}>
                              {invoice.scheduleItem.siteAddress}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" className={shared.primaryText}>
                            {invoice.acreage != null ? `${invoice.acreage} ac` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" className={shared.primaryText}>
                            {formatCurrency(invoice.finalAmount ?? invoice.amount)}
                          </Typography>
                          {invoice.adjustments && invoice.adjustments.length > 0 ? (
                            <Typography variant="body2" className={shared.secondaryText}>
                              {invoice.adjustments.length} adj.
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {invoice.pricingStatus === 'needs_review' ? (
                            <Typography variant="body2" style={{ color: '#8c5b00', fontWeight: 600 }}>
                              Office review
                            </Typography>
                          ) : (
                            <StatusBadge status={invoice.status} classes={classes} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={shared.primaryText}>
                            {displayDate}
                          </Typography>
                          <Typography variant="body2" className={shared.secondaryText}>
                            {dateLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gridGap={4}>
                            {invoice.invoicePdfUrl ? (
                              <Link
                                href={invoice.invoicePdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.downloadLink}
                                underline="always"
                              >
                                Download Invoice
                              </Link>
                            ) : null}
                            {invoice.subSprayLogPdfUrl ? (
                              <Link
                                href={invoice.subSprayLogPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.downloadLink}
                                style={{ fontWeight: 400, fontSize: 12, color: 'var(--ink-3)' }}
                                underline="always"
                              >
                                Spray Log
                              </Link>
                            ) : null}
                            {!invoice.invoicePdfUrl && !invoice.subSprayLogPdfUrl && (
                              <Typography variant="body2" className={shared.secondaryText}>—</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}

                  {!invoices.length ? (
                    <TableRow>
                      <TableCell colSpan={7} className={classes.emptyCell}>
                        <PortalStateCard
                          title="No invoices yet"
                          message={status ? 'No invoices match the selected status filter.' : 'Your invoices will appear here once they have been generated.'}
                          tone="muted"
                          minHeight={180}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorInvoices)
